<template>
  <div>
    <b-modal  v-model="isOpen"
              size="lg"
              centered
              body-class="scroll-y"
              header-class="modal-header-full"
              footer-class="d-none"
              hide-header-close
              scrollable>

      <template #title>
        <div class="header-layout">
          <div class="header-left">
            <h2 class="modal-title">
              <strong v-if="creationMode">{{ $t('Webportal.KpiSearch.NewViewAddKpis') }}</strong>
              <strong v-else>{{ boardSetup.label }}</strong>
              <Pencil :size="14" class="edit-icon clickable ms-1 mb-1" @click="delegateEditBoardDescription({ index: boardSetup.id })" />
            </h2>
            <p class="modal-description">
              <template v-if="creationMode">
                {{ $t('Webportal.KpiSearch.AddKpis1') }} "{{ pendingBoardSettings?.name }}"
              </template>
              <template v-else>
                {{ boardSetup.description }}
              </template>
            </p>
          </div>
          <div class="header-actions">
            <b-button variant="outline-secondary" size="sm" class="btn-height"
                      @click="isOpen = false">
              {{ $t('Webportal.Cancel')}}
            </b-button>
            <b-button variant="primary" size="sm" class="btn-height"
                      :disabled="instanceCount > 12"
                      @click="handleOk()">
              {{ $t('Webportal.Apply')}}
            </b-button>
          </div>
        </div>
      </template>

      <template #default>
        <BOverlay :show="!isClientReady || !isReady" variant="dark" opacity="0.5">
          <template #overlay>
            <default-spinner />
          </template>
          <KpiSearchBar :items="kpiSearchOptions"
                        v-model="kpiSearchQuery"
                        :placeholder="$t('Webportal.KpiSearch.Placeholder')"
                        @select="onKpiSelected"
                        class="mb-3" />

          <div class="add-kpi-content">
            <!-- Left: Available KPIs -->
            <div class="available-kpis-section">
              <div class="section-header">
                <h3 class="section-title">{{ $t('Webportal.KpiSearch.AvailableKpis') }}</h3>
              </div>

              <div class="kpi-categories-scroll">
                <div class="kpi-categories">
                  <!-- Line Break special item -->
                  <div class="kpi-group-accordion">
                    <div class="kpi-group-header">
                      <div class="kpi-linebreak-indicator"></div>
                      <div class="kpi-header-content flex-grow-1">
                            <h4 class="item-title mb-0">{{ $t('Webportal.KpiSearch.LineBreak') }}</h4>
                        <p class="item-subtitle mb-0">&nbsp;</p>
                      </div>
                      <Plus :size="20" class="add-icon clickable" @click="addLineBreak()" />
                    </div>
                  </div>

                  <div  v-for="item in kpiOptionsProp"
                        :key="item.key"
                        class="kpi-group-accordion">
                    <!-- KPI Header -->
                    <div  class="kpi-group-header">
                      <div :class="[!item.kpiLabel ? 'kpi-group-indicator' : 'kpi-indicator']"></div>
                      <div class="kpi-header-content flex-grow-1">
                        <h4 class="item-title mb-0">{{ item.kpiGroupLabel }}</h4>
                        <p class="item-subtitle mb-0">{{ item.kpiLabel || '\u00A0' }}</p>
                      </div>
                      <Plus :size="20" class="add-icon clickable"  @click="showDefineKpiModal(item, false)" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: Added KPIs -->
            <div class="added-kpis-section">
              <div class="section-header">
                <div>
                  <h3 class="section-title" :class="{ 'limit-reached': instanceCount > 12 }">{{ $t('Webportal.KpiSearch.AddedKpis') }} ({{ instanceCount }}/12)</h3>
                </div>
              </div>

              <div class="added-kpis-scroll">
                <div v-if="controls.length === 0" class="empty-state">
                  <p class="empty-state-text">
                    {{ $t('Webportal.KpiSearch.NoKpisYet') }}
                  </p>
                </div>
                <draggable  v-else
                            v-model="controls"
                            :item-key="(item, index) => `kpi-${item.kpiId || item.kpiCode}-${index}`"
                            handle=".drag-handle"
                            :animation="200"
                            ghost-class="ghost-item"
                            class="added-kpis-list">
                  <template #item="{ element: kpi, index }">
                    <div class="added-kpi-item">
                      <div class="added-kpi-header">
                        <div class="drag-handle">
                          <GripVertical :size="16" />
                        </div>
                        <template v-if="kpi.type === 'linebreak'">
                          <div class="added-kpi-info">
                            <p class="item-title">{{ $t('Webportal.KpiSearch.LineBreak') }}</p>
                          </div>
                        </template>
                        <template v-else>
                          <div class="added-kpi-info">
                            <p class="item-title">{{ kpi.kpiCode || kpi.kpiGroupCode }}</p>
                            <p class="item-subtitle">{{ kpi.label }}</p>
                          </div>
                          <Pencil :size="14" class="edit-icon clickable me-1" @click="showDefineKpiModal(kpi, true)" />
                        </template>
                        <X :size="20" class="remove-icon clickable" @click="removeKpi(index)" />
                      </div>
                    </div>
                  </template>
                </draggable>
              </div>
            </div>
          </div>
        </BOverlay>
      </template>

    </b-modal>

    <Teleport to="body">
      <ModalDefineKpi v-if="showModalDefineKpi"
                      v-model="showModalDefineKpi"
                      :kpi="selectedKpi"
                      :is-edit-mode="isEditMode"
                      :wave-options="waveOptions"
                      :sub-kpi-options="[]" />
    </Teleport>
  </div>
</template>

<script>
import { Globe, Activity, Layers, Plus, X, CirclePlus, Save, GripVertical, Trash2, Pencil } from 'lucide-vue-next';
import draggable from 'vuedraggable';
import KpiSearchBar from '@/componentsv3/KpiSearchBar.vue';
import KpiGroupDetails from './KpiGroupDetails.vue';
import KpiDetails from './KpiDetails.vue';
import SurveysService from '@/services/SurveysService';
import { mapGetters } from 'vuex';
import DefaultSpinner from '@/componentsv3/DefaultSpinner.vue';
import ModalDefineKpi from './ModalDefineKpi.vue';

export default {
  name: 'ModalCustomizeKpis',
  inject: ['delegateCustomizedBoard', 'delegateDeleteView', 'delegateEditBoardDescription', 'delegateCreateBoardWithKpis'],
  provide() {
    return {
      delegateAddKpi: this.inboundAddKpi
    }
  },

  emits: ['update:modelValue'],

  components: {
    Globe, Activity, Layers, Plus, X, CirclePlus, Save, GripVertical, Trash2, Pencil,
    draggable,
    KpiSearchBar,
    KpiGroupDetails,
    KpiDetails,
    DefaultSpinner,
    ModalDefineKpi
  },

  props: {
    modelValue:            { type: Boolean, default: false },
    kpis:                  { type: Array, default: () => [] },
    index:                 { type: Number, required: true },
    boardSetup:            { type: Object, required: true },
    creationMode:          { type: Boolean, default: false },
    pendingBoardSettings:  { type: Object, default: null }
  },

  data() {
    return {
      controls: JSON.parse(JSON.stringify(this.kpis)),
      isEditMode: false,

      // Options Data from API
      countryOptions: [],
      waveOptions: [],
      kpiOptions: [],

      kpi: null,
      kpiSearchOptions: [],
      kpiSearchQuery: '',
      selectedKpi: null,
      showModalDefineKpi: false
    };
  },

  async mounted() {
    const [waves, options] = await Promise.all([
      this.loadWaves(),
      this.loadKpiOptions()
    ]);

    this.waveOptions = waves;
    this.countryOptions = this.countryCodes.map(code => ({ id: code, label: code }));
    this.kpiOptions = this.getKpiOptions(options);
    this.kpiSearchOptions = this.getKpiSearchOptions(options);

    await this.$nextTick();
  },

  computed: {
    ...mapGetters('auth', [ 'clientId' ]),
    ...mapGetters('localization', [ 'currentLocale' ]),
    ...mapGetters('clientData', [ 'isClientReady', 'project', 'surveyIds', 'countryCodes' ]),

    isOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },

    kpiOptionsProp() {
      const query = this.kpiSearchQuery.toLowerCase();
      return this.kpiOptions.filter(k =>
        k.label.toLowerCase().includes(query) ||
        this.kpiSearchQuery === ''
      );
    },

    isReady() {
      return this.isClientReady && this.kpiOptions.length > 0
          && this.waveOptions && this.waveOptions.length > 0;
    },

    instanceCount() {
      return this.controls.filter(c => c.type !== 'linebreak').length;
    }
  },

  methods: {
    addLineBreak() {
      this.controls.push({
        type: 'linebreak',
        label: 'Line Break',
        kpiId: 999999999,
        kpiCode: 'none',
        kpiGroupId: 999999999,
        kpiGroupCode: 'none',
        position: 0,
        isActive: true,
        filter: JSON.stringify({}),
        config: JSON.stringify({}),
      });
      this.reassignPositions();
    },

    showDefineKpiModal(kpi, isEdit = false) {
      this.selectedKpi = { ...kpi };
      this.isEditMode = isEdit;
      this.showModalDefineKpi = true;
    },

    async onKpiSelected(selectedKpi) {
      this.showDefineKpiModal(selectedKpi, false);
    },

    showEditBoardDescriptionModal(kpi, isEdit = false) {
      this.selectedKpi = { ...kpi };
      this.isEditMode = isEdit;
      this.showModalDefineKpi = true;
    },

    reassignPositions() {
      let position = 1;
      this.controls.forEach(kpi => {
        kpi.position = position++;
      });
    },

    getKpiOptions(surveys) {
      if (!surveys || surveys.length === 0)
        return [];

      const items = new Map();
      surveys.forEach(s => {
        s.children.forEach(t => {
          t.children.forEach(g => {
            // Kpi Groups
            if (!items.has(g.kpiGroupId)) {
              items.set(g.kpiGroupId, {
                key: `${g.kpiGroupId}`,
                label: `${g.label}`,
                kpiGroupId: g.kpiGroupId,
                kpiGroupCode: g.kpiGroupCode,
                kpiGroupLabel: g.label
              });
            }
            g.children.forEach(k => {
              // Kpis
              const compositeKey = `${g.kpiGroupId}-${k.kpiId}`;
              if (!items.has(compositeKey)) {
                items.set(compositeKey, {
                  key: compositeKey,
                  label: `${g.label} - ${k.label}`,
                  kpiGroupId: g.kpiGroupId,
                  kpiGroupCode: g.kpiGroupCode,
                  kpiGroupLabel: g.label,
                  kpiId: k.kpiId,
                  kpiCode: k.kpiCode,
                  kpiLabel: k.label,
                });
              }
            });
          });
        });
      });

      return _.orderBy(Array.from(items.values()), ['label'], ['asc']);
    },

    getKpiSearchOptions(kpis) {
      if (!kpis || kpis.length === 0)
        return [];
      const groupSet = new Set();
      const kpiSet = new Set();
      const results = [];

      try {
        kpis.forEach(survey => {
          survey.children.forEach(tranche => {
            tranche.children.forEach(group => {
              const g = group;
              if (!groupSet.has(g.label)) {
                groupSet.add(g.label);
                results.push({
                  label: g.label,
                  group: g.label,
                  type: 'group',
                  kpiGroupId: g.kpiGroupId,
                  kpiGroupCode: g.kpiGroupCode,
                });
              }
              group.children.forEach(kpi => {
                const kpiLabel = g.label + ' => ' + kpi.label;
                if (!kpiSet.has(kpiLabel)) {
                  kpiSet.add(kpiLabel);
                  results.push({
                    label: kpiLabel,
                    group: g.label,
                    kpi: kpi.label,
                    type: 'kpi',
                    kpiId: kpi.kpiId,
                    kpiCode: kpi.kpiCode
                  });
                }
              });
            });
          });
        });
      } catch(e) {
        log.error("Error in getKpiSearchOptions:", e);
      }
      return _.orderBy(results, ['type', 'label'], ['asc', 'asc']);
    },

    async loadKpiOptions() {
      const kpiResponse = await SurveysService.getSurveyKpisWithIds(
        { surveyIds: this.surveyIds }, this.currentLocale);
      return kpiResponse.data || [];
    },

    async loadWaves() {
      const waveFilter = {
        clientIds: this.clientId,
        projectIds: this.project ? this.project.ClientProject.ClientProjectId : null,
        surveyIds: this.surveyIds,
      };

      const waveResponse = await SurveysService.getSurveyWaves(
        waveFilter, this.currentLocale);

      let waveResults = [];
      try {
      waveResponse.data.forEach(survey => {
        survey.children.forEach(w => {
          const wave = waveResults.find(s => s.label === w.label);
          if (wave) {
            wave.surveyIds.push(w.id);
          }
          else {
            waveResults.push( { id: w.label, label: w.label, surveyIds:[w.id], SortOrder: w.SortOrder } );
          }
        });
      });
      }
      catch(e) {
        log.error("Error in loadWaves:", e);
      }
      return _.orderBy(waveResults || [], ['SortOrder'], ['desc']);
    },

    getSubKpiOptions(kpi) {
      return kpi.subKpis.map(sub => ({ value: sub, text: sub }));
    },

    inboundAddKpi(data) {
      this.showModalDefineKpi = false;
      if (!data.isEdit) {
        // Add new KPI instance
        let item = {
          label: data.kpi.label,
          kpiGroupId: data.kpi.kpiGroupId,
          kpiGroupCode: data.kpi.kpiGroupCode,
          kpiId: data.kpi.kpiId,
          kpiCode: data.kpi.kpiCode,
          filter: JSON.stringify(data.selection),
          config: JSON.stringify({}), // TODO: add config options here
          // position: 0, // Position will be set on save (monotonically increasing)
          // id: 0, // ID will be set by backend
          type: data.kpi.kpiId ? 'kpi' : 'group',
          isActive: data.kpi.isActive || true,
        };
        log.warn('KPI added:', item);
        this.controls.push(item);
        this.reassignPositions();
      } else {
        // Edit existing KPI instance
        const item = this.controls.find(k => k.position === data.kpi.position);
        if (!item) {
          throw new Error("KPI to edit not found at position " + data.kpi.position);
        }
        let itemOld = { ...item };
        item.label = data.kpi.label;
        item.kpiGroupId = data.kpi.kpiGroupId;
        item.kpiGroupCode = data.kpi.kpiGroupCode;
        item.kpiId = data.kpi.kpiId;
        item.kpiCode = data.kpi.kpiCode;
        item.filter = JSON.stringify(data.selection);
        item.config = JSON.stringify({}); // TODO: add config options here
        // position: 0, // Position will be set on save (monotonically increasing)
        // id: 0, // ID will be set by backend
        item.type = data.kpi.type;
        item.isActive = data.kpi.isActive;

        log.warn('KPI edited:', itemOld, '->', item);
      }
    },

    editKpi(index) {
      const kpi = this.controls[index];
      // TODO: KPI bearbeiten - Modal öffnen mit vorhandenen Daten
      log.warn('Edit KPI:', kpi);
    },

    removeKpi(index) {
      this.controls.splice(index, 1);
    },

    formatTrend(trend) {
      if (trend === 0) return '0pp';
      const sign = trend > 0 ? '+' : '';
      return `${sign}${trend}pp`;
    },

    handleOk() {
      // Positionen final neu setzen (für Drag-and-Drop Reihenfolge)
      this.reassignPositions();

      if (this.creationMode) {
        // Creation Mode: Board erstellen UND KPIs speichern (sequentiell)
        this.delegateCreateBoardWithKpis({
          boardSettings: this.pendingBoardSettings,
          kpis: [...this.controls]
        });
      } else {
        // Edit Mode: Nur KPIs aktualisieren (wie bisher)
        this.delegateCustomizedBoard({ kpis: [...this.controls], index: this.index });
      }

      this.controls = [];
      this.isOpen = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-title strong {
  display: inline-block;
  max-width: 85%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.add-kpi-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  height: 600px;
}

.available-kpis-section,
.added-kpis-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 24rem;
}

.available-kpis-section {
  border-right: 1px solid var(--color-border);
  padding-right: 16px;
}

.added-kpis-section {
  padding-left: 16px;
}

.section-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0;
}

.kpi-categories-scroll,
.added-kpis-scroll {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
}

.kpi-categories {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.kpi-group-accordion {
  // border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  overflow: visible;
}

.kpi-group-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 12px;
  //background: #f9fafb;
  transition: background-color 0.15s;
  border-radius: var(--radius-xs);
  background-color: var(--custom-black-05);
  border: 1px solid var(--custom-black-10);
  &:hover {
    border-color: var(--custom-black-50);
  }
  &.clickable {
    cursor: pointer;
    user-select: none;

    &:hover {
    //  background: #f3f4f6;
    }
  }
}

.kpi-header-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.kpi-group-indicator,
.kpi-indicator {
  width: 4px;
  height: 24px;
  border-radius: 2px;
}

.kpi-group-indicator {
  background: var(--bmw-primary);
}
.kpi-indicator {
  background: var(--color-muted-foreground);
}
.kpi-linebreak-indicator {
  width: 4px;
  height: 24px;
  border-radius: 2px;
  background: var(--bs-border-color);
}

.item-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 95%;
}
.item-subtitle {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0;
  font-weight: 400;
    overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 95%;
}

.kpi-count {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
}

.add-icon {
  color: var(--color-muted-foreground);
  transition: color 0.15s;

  .kpi-group-header:hover & {
    color: var(--bmw-primary);
  }
}

.kpi-group-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.load-more-container {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.added-kpis-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.added-kpi-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 12px;
  height: 3rem;
  background-color: var(--custom-black-05);
  border: 1px solid var(--custom-black-10);
  border-radius: var(--radius-sm);
  transition: all 0.15s;
  cursor: default;

  &:hover {
    border-color: var(--custom-black-50);
  }
}
[data-bs-theme="dark"] {
  .added-kpi-item {
    background-color: var(--custom-white-05);
    border: 1px solid var(--custom-white-10);
    &:hover {
      border-color: var(--custom-white-20);
    }
  }
}

.ghost-item {
  opacity: 0.5;
  background: rgba(var(--bmw-primary-rgb), 0.1);
  border: 2px dashed var(--bmw-primary);
}

.drag-handle {
  display: flex;
  align-items: center;
  cursor: grab;
  color: var(--color-muted-foreground);
  padding: 0 4px;
  margin-right: 8px;
  transition: color 0.15s;

  &:hover {
    color: var(--bmw-primary);
  }

  &:active {
    cursor: grabbing;
  }
}

.added-kpi-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.added-kpi-info {
  flex: 1;
  min-width: 0;
}

.edit-icon,
.remove-icon {
  color: var(--color-muted-foreground);
  transition: color 0.15s;

  &:hover {
    color: var(--bmw-primary);
  }
}

.added-kpi-badges {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.kpi-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  background: #f9fafb;
  color: #374151;
  border: 1px solid var(--color-border);
  border-radius: 4px;

  &-muted {
    color: var(--color-muted-foreground);
    border-color: var(--color-border);
  }
}

.added-kpi-values {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--color-muted-foreground);
  flex-wrap: wrap;
}

.value-item {
  white-space: nowrap;
}

.value-current {
  color: var(--bmw-primary);
}

.value-separator {
  color: #d1d5db;
}

.value-trend {
  font-weight: 500;
  color: var(--color-muted-foreground);
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: 24px;
  text-align: center;
}

.empty-state-text {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
}

// Dark Mode
[data-bs-theme="dark"] {
  .section-title,
  .kpi-item-name,
  .item-title {
    color: var(--color-foreground);
  }

  .kpi-item-description,
  .item-subtitle,
  .added-kpi-values,
  .empty-state-text {
    color: var(--color-muted-foreground);
  }

  .available-kpis-section {
    border-right-color: var(--color-border);
  }

  .kpi-item {
    background: var(--color-card);
    border-color: var(--color-border);
  }

  .kpi-badge {
    background: var(--color-muted);
    color: var(--color-foreground);
    border-color: var(--color-border);
  }

  .value-current {
    color: var(--color-primary);
  }

  .kpi-group-header {
    background-color: var(--custom-white-05);
    border: 1px solid var(--custom-white-10);
    &:hover {
      border-color: var(--custom-white-20);
    }

  }

}

.limit-reached {
  color: var(--default-status-red)!important;
}

</style>

